// The link wizards' candidate fetch: one request per selected row, intersected.
import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from '@/app/hooks';
import { intersectLinkableRows } from '@/lib/mappers/links';
import { EMPTY_LIST } from '@/lib/utils';

/**
 * Each linkable query answers for ONE parent id, so N selected rows need N requests and a
 * hook cannot loop — hence RTK Query's imperative path. A candidate has to be linkable to
 * EVERY selected row, so rows that disagree share nothing and the wizard opens on its
 * "nothing can be linked" notice. `argsList` must be memoised; its identity refires the fetch.
 */
export const useLinkableCandidates = (endpoint, argsList = EMPTY_LIST, enabled = true) => {
  const dispatch = useAppDispatch();
  const [pages, setPages] = useState(null);

  useEffect(() => {
    // Also the reset: closing the wizard drops the last selection's answers.
    setPages(null);
    if (!enabled || argsList.length === 0) return undefined;
    let live = true;
    const requests = argsList.map((args) => dispatch(endpoint.initiate(args)));
    Promise.all(requests).then((results) => {
      if (live) setPages(results.map((result) => result.data));
    });
    return () => {
      live = false;
      requests.forEach((request) => request.unsubscribe());
    };
  }, [enabled, argsList, endpoint, dispatch]);

  const candidates = useMemo(
    () => (pages == null ? EMPTY_LIST : intersectLinkableRows(pages)),
    [pages],
  );

  return { candidates, loading: argsList.length > 0 && pages == null };
};
